import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Loading, Empty, ScreenBoundary } from "../components/states";
import {
  Button,
  Chips,
  CollapsibleSection,
  Footnote,
  SectionHeading,
  SelectableRow,
  Sheet,
  StatusPill,
  SubHeading,
  UrgencyTag,
  plural,
  relativeDay,
  shortDate,
} from "../components/kit";
import { useBusyAction } from "../lib/useBusyAction";
import { cn } from "@/lib/utils";
import { Card as UiCard } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { TONE_EDGE } from "@shared/status";

/**
 * The people who actually deliver, off the seeded dispatch history
 * (`convex/seed.ts` — `rng.pick(["Aileen", "Peter", "Courier — Delta DC"])`).
 * Real names out of the data, not invented ones; anybody else is typed.
 */
const COURIERS = ["Aileen", "Peter", "Courier — Delta DC"] as const;

/** The explicit "we are not recording a name" answer. It is a *choice she
 *  makes*, not a blank field — see `DeliveredBy`. */
const NOT_RECORDED = "Not recorded";
const SOMEONE_ELSE = "Someone else…";

/** What the screen holds onto after a dispatch goes out short. See
 *  `Discrepancies`. */
type Discrepancy = {
  number: string;
  clinicName: string;
  notes: string[];
  at: number;
};

/**
 * The warehouse screen.
 *
 * Two halves: what still needs picking, grouped by clinic, and what has already
 * gone out. The pick list is the thing that replaces reading five spreadsheets
 * to work out what goes on the van, so it leads — and it shows availability
 * next to each item, because discovering a shortfall while packing is far
 * cheaper than discovering it after the clinic has been told it's coming.
 *
 * One clinic is one card and one van drop, so each clinic folds independently
 * and carries its own always-present send button rather than one that
 * materialises when enough items happen to be ticked. A button that appears is a
 * button that has to be found; a disabled one that says what it is waiting for
 * is not.
 *
 * **Nothing on this screen says "line".** A line is a row in a database; what
 * she is packing is an item. The counting copy is items and units throughout,
 * and every count goes through `plural` so "1 lines · 1 units" cannot come back.
 */
export function Dispatch() {
  return (
    <ScreenBoundary
      title="The pick list could not load"
      heading={<SectionHeading title="Dispatch" />}
      onError={(error, info) =>
        console.error("Dispatch screen failed:", error, info.componentStack)
      }
    >
      <DispatchScreen />
    </ScreenBoundary>
  );
}

/* -------------------------------------------------------------------------- */
/* Who is delivering                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Who is dropping it off — as a decision, not as an empty box.
 *
 * Gloria's spec lists "Delivered by" as a field to capture, and "who dropped it
 * off?" is exactly the question asked when a delivery is disputed. This was a
 * free-text field that could be left blank, and a blank field is the shape of
 * problem where the *record* ends up carrying whatever the code happened to
 * default to.
 *
 * So there is no blank state that can be dispatched from. `value` is `null`
 * until she picks something, the send button refuses to work until then, and
 * "Not recorded" is one of the choices she can make — an honest record that says
 * nobody was named beats a record that quietly names somebody who did not drive.
 *
 * Free text survives as an option because the courier is sometimes none of the
 * three; it opens in a sheet rather than sitting on the page, so the pick list
 * is not carrying a keyboard-summoning input above it all morning.
 */
function DeliveredBy({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [typed, setTyped] = useState("");

  const known: string[] = [...COURIERS, NOT_RECORDED];
  const isOther = value !== null && !known.includes(value);

  const options = [
    ...known.map((name) => ({ value: name, label: name })),
    { value: SOMEONE_ELSE, label: isOther ? value : SOMEONE_ELSE },
  ];

  return (
    /* Stock `ui/card`, laid out as a plain block: `p-3` and `gap-0` beat the
       registry's `py-6`/`gap-6`, which exist for the header-and-content shape
       this card does not have. */
    <UiCard className="block flex-row gap-0 space-y-2 p-3">
      <SubHeading>Who is delivering?</SubHeading>
      <Chips<string>
        value={isOther ? SOMEONE_ELSE : (value ?? "")}
        onChange={(name) => {
          if (name === SOMEONE_ELSE) {
            setTyped(isOther ? value : "");
            setSheetOpen(true);
            return;
          }
          onChange(value === name ? null : name);
        }}
        options={options}
      />
      <p className="text-xs text-muted-foreground">
        {value === null
          ? "Pick one before sending. It goes on the delivery record."
          : value === NOT_RECORDED
            ? "This delivery will show as Unassigned on the record."
            : `Recorded on everything you send: ${value}.`}
      </p>

      <Sheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title="Who is delivering?"
        description="A name goes on the delivery record and answers “who dropped it off?” later."
        footer={
          <Button
            size="lg"
            className="w-full"
            disabled={typed.trim().length === 0}
            onClick={() => {
              onChange(typed.trim());
              setSheetOpen(false);
            }}
          >
            Use this name
          </Button>
        }
      >
        {/* shadcn's `<Input>` as shipped. The registry base is already
            `text-base md:text-sm`, so a phone keyboard does not zoom the
            viewport, and the focus ring is `--ring`. Nothing left to add. */}
        <Input
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          placeholder="Name of the person or company"
          aria-label="Delivered by"
        />
      </Sheet>
    </UiCard>
  );
}

/* -------------------------------------------------------------------------- */
/* Stock that was not there                                                    */
/* -------------------------------------------------------------------------- */

/**
 * What went out short, still on screen afterwards.
 *
 * The server reports a shortfall when it dispatches more than the count said was
 * on the shelf. That used to be a warning toast, which is gone in four seconds
 * and lands nowhere — the one moment the app knows the physical shelf and the
 * system disagree, and it was told to nobody in a way that survived a scroll.
 *
 * This is the honest half-measure: it stays on screen until she dismisses it,
 * so she can walk to the bin and count. It is **not** persisted — `dispatches`
 * has no field for it and adding one is a schema change this screen's owner
 * does not get to make. That limitation is stated on the card rather than
 * implied away, because a "recorded" discrepancy that vanishes on refresh is
 * worse than one that admits what it is.
 */
function Discrepancies({
  items,
  onDismiss,
}: {
  items: Discrepancy[];
  onDismiss: (number: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="space-y-2">
      {items.map((entry) => (
        <UiCard
          key={entry.number}
          /* Stock `ui/card` as a plain block at `p-3`, as above. The red edge is
             the app's one red rule, from `TONE_EDGE` — spelling it as a raw
             `border-red-500` here is how the same "somebody must act" signal
             drifts into a second shade. */
          className={cn("block flex-row gap-0 border-l-4 p-3", TONE_EDGE.red)}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-sm font-bold text-red-800">
                Count check — {entry.clinicName} ({entry.number})
              </h2>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Sent {shortDate(entry.at)}. More went out than the system thought
                was on the shelf, so these counts are wrong somewhere:
              </p>
            </div>
            {/* Stock `ghost`, pulled into the card's corner by the negative
                margins. Dismissing a note is not the card's subject. */}
            <Button
              variant="ghost"
              onClick={() => onDismiss(entry.number)}
              className="-mt-1 -mr-2 shrink-0"
            >
              Done
            </Button>
          </div>
          <ul className="mt-2 space-y-1">
            {entry.notes.map((note) => (
              <li key={note} className="text-sm font-medium">
                {note}
              </li>
            ))}
          </ul>
          <Footnote tone="caution" className="mt-2">
            Recount these on the shelf and book the difference in on Warehouse.
            This note lives on this screen only — it is not saved with the
            delivery yet.
          </Footnote>
        </UiCard>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The screen                                                                  */
/* -------------------------------------------------------------------------- */

function DispatchScreen() {
  const pick = useQuery(api.dispatch.pickList, {});
  const recent = useQuery(api.dispatch.list, { limit: 15 });
  const create = useMutation(api.dispatch.create);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { busy, run } = useBusyAction();
  const [deliveredBy, setDeliveredBy] = useState<string | null>(null);
  const [discrepancies, setDiscrepancies] = useState<Discrepancy[]>([]);
  /** The short item waiting on a "yes, send it anyway". */
  const [confirmShort, setConfirmShort] = useState<{
    id: string;
    itemName: string;
    qty: number;
    onHand: number;
  } | null>(null);

  if (pick === undefined || recent === undefined) {
    return (
      <div data-screen-state="loading" className="space-y-4">
        <SectionHeading title="Dispatch" />
        <Loading rows={4} label="Loading the pick list" />
      </div>
    );
  }

  const itemsWaiting = pick.reduce(
    (total, group) => total + group.lines.length,
    0,
  );

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  /**
   * Ticking an item the shelf cannot cover is a decision, not a tap.
   *
   * "Select all" skips these outright and the row asks before it joins the van,
   * because dispatching stock that does not exist silently is how a clinic is
   * told something is coming that nobody can put in a box — and how the stock
   * count quietly goes negative.
   */
  const tryToggle = (line: (typeof pick)[number]["lines"][number]) => {
    if (line.shortfall && !selected.has(line.requestLineId)) {
      setConfirmShort({
        id: line.requestLineId,
        itemName: line.itemName,
        qty: line.qty,
        onHand: line.warehouseOnHand,
      });
      return;
    }
    toggle(line.requestLineId);
  };

  const dispatchClinic = (group: (typeof pick)[number]) => {
    const lines = group.lines.filter((line) => selected.has(line.requestLineId));
    if (lines.length === 0 || deliveredBy === null) return;

    return run(
      async () => {
        const result = await create({
          clinicId: group.clinicId,
          /* Never a placeholder. "Not recorded" is her explicit choice and the
             mutation stores "Unassigned" for an empty string — see
             convex/dispatch.ts. The grey hint text of a field has no business
             becoming a row in the database. */
          deliveredBy: deliveredBy === NOT_RECORDED ? "" : deliveredBy,
          lines: lines.map((line) => ({
            itemId: line.itemId,
            qty: line.qty,
            requestLineId: line.requestLineId,
          })),
        });

        /* Success-only, so it lives at the end of the action rather than after
           the await: neither the count check nor the cleared ticks may survive
           a refusal. */
        if (result.shortfalls.length > 0) {
          setDiscrepancies((current) => [
            {
              number: result.number,
              clinicName: group.clinicName,
              notes: result.shortfalls,
              at: Date.now(),
            },
            ...current,
          ]);
        }
        setSelected(new Set());
        return result;
      },
      {
        failed: `Could not send this delivery to ${group.clinicName}`,
        done: (result) => {
          const happened = `${result.lineCount} ${plural(
            result.lineCount,
            "item",
          )} on the way to ${group.clinicName}`;

          if (result.shortfalls.length > 0) {
            return {
              happened,
              next:
                `${result.number} is out, but the shelf count did not add up — see the ` +
                "count check at the top of the screen.",
              check: true,
            };
          }
          return {
            happened,
            next:
              result.lowStockAlerts > 0
                ? `${result.number} raised. ${result.lowStockAlerts} ${plural(
                    result.lowStockAlerts,
                    "item",
                  )} dropped below minimum — they are on the warehouse reorder list.`
                : `${result.number} raised. ${group.clinicName} confirms receipt when it arrives.`,
          };
        },
      },
    );
  };

  return (
    <div data-screen-state="ready" className="space-y-5 pb-8">
      <SectionHeading
        title="Dispatch"
        subtitle={
          itemsWaiting === 0
            ? "Nothing waiting to go out"
            : `${itemsWaiting} ${plural(itemsWaiting, "item")} to pack for ` +
              `${pick.length} ${plural(pick.length, "clinic")}`
        }
      />

      <Discrepancies
        items={discrepancies}
        onDismiss={(number) =>
          setDiscrepancies((current) =>
            current.filter((entry) => entry.number !== number),
          )
        }
      />

      {/* The pick list is the work; what already went out is reference. At
          desktop they sit side by side rather than one below a hundred rows of
          the other. */}
      <div className="lg:grid lg:grid-cols-3 lg:items-start lg:gap-6">
        <div className="space-y-3 lg:col-span-2">
          {pick.length > 0 ? (
            <DeliveredBy value={deliveredBy} onChange={setDeliveredBy} />
          ) : null}

          {pick.length === 0 ? (
            <Empty
              tone="good"
              title="Nothing to pick"
              body="Every approved request has been dispatched. Approved items appear here automatically."
            />
          ) : (
            pick.map((group) => {
              /* One walk of the clinic's lines, not three. Every tick
                 re-renders this loop, and "ticked", "on the shelf" and "is
                 everything on the shelf ticked" are three answers to the same
                 pass. */
              const inStock: typeof group.lines = [];
              let chosenCount = 0;
              let inStockChosen = 0;
              for (const line of group.lines) {
                const ticked = selected.has(line.requestLineId);
                if (ticked) chosenCount += 1;
                if (!line.shortfall) {
                  inStock.push(line);
                  if (ticked) inStockChosen += 1;
                }
              }
              const allInStockChosen =
                inStock.length > 0 && inStockChosen === inStock.length;
              const short = group.lines.length - inStock.length;

              return (
                <CollapsibleSection
                  key={group.clinicId}
                  title={group.clinicName}
                  count={group.lines.length}
                  tone={short > 0 ? "red" : "gray"}
                  sticky={false}
                  summary={
                    `${group.lines.length} ${plural(group.lines.length, "item")} to pack` +
                    (short > 0
                      ? ` · ${short} not on the shelf`
                      : chosenCount > 0
                        ? ` · ${chosenCount} ticked`
                        : "")
                  }
                >
                  {/* Selecting everything means everything the shelf can
                      actually cover. It used to include items with nothing on
                      hand, which is a tap that promises a clinic a box nobody
                      can pack. */}
                  <div className="flex items-center justify-between gap-3 border-b px-3 py-1.5">
                    {/* This line has to follow the ticking, not sit above it
                        repeating an instruction she has already carried out. It
                        read "Tick what goes on the van" with eight of eight
                        ticked, which tells a person they have not done the
                        thing they just did — so once anything is ticked it
                        reports the count, and says what is still missing only
                        when something genuinely is. */}
                    <span className="text-xs text-muted-foreground">
                      {chosenCount > 0
                        ? `${chosenCount} of ${group.lines.length} ticked` +
                          (deliveredBy === null ? " · pick a courier above" : "")
                        : short > 0
                          ? `${short} ${plural(short, "item")} not on the shelf — tick ` +
                            "those one at a time"
                          : "Tick what goes on the van"}
                    </span>
                    {/* Stock `ghost` with the accent on the label — a
                        secondary convenience beside the row it acts on, not a
                        second primary action. Disabled is the registry's own
                        fade. */}
                    <Button
                      variant="ghost"
                      disabled={inStock.length === 0}
                      onClick={() =>
                        setSelected((current) => {
                          const next = new Set(current);
                          for (const line of inStock) {
                            allInStockChosen
                              ? next.delete(line.requestLineId)
                              : next.add(line.requestLineId);
                          }
                          return next;
                        })
                      }
                      className="shrink-0 text-primary"
                    >
                      {allInStockChosen ? "Clear" : "Select in stock"}
                    </Button>
                  </div>

                  <div className="divide-y">
                    {group.lines.map((line) => {
                      const isSelected = selected.has(line.requestLineId);
                      return (
                        <SelectableRow
                          key={line.requestLineId}
                          selected={isSelected}
                          onToggle={() => tryToggle(line)}
                        >
                          <span className="flex items-baseline gap-2">
                            <span className="num w-8 shrink-0 text-right font-bold">
                              ×{line.qty}
                            </span>
                            <span className="min-w-0 flex-1 truncate text-sm font-semibold">
                              {line.itemName}
                            </span>
                          </span>
                          <span className="mt-1 flex flex-wrap items-center gap-2 pl-10 text-xs">
                            <span
                              className={
                                line.shortfall
                                  ? "font-semibold text-red-700"
                                  : "text-muted-foreground"
                              }
                            >
                              {line.warehouseOnHand} on hand
                              {line.shortfall ? ` — ${line.qty} asked for` : ""}
                            </span>
                            <UrgencyTag urgency={line.urgency} />
                            <span className="text-muted-foreground">
                              asked {relativeDay(line.requestedAt)}
                            </span>
                          </span>
                        </SelectableRow>
                      );
                    })}
                  </div>

                  {/* Always here, so the primary action has one place on the
                      screen instead of appearing under the thumb once enough
                      items are ticked. */}
                  <div className="border-t bg-muted/50 p-3">
                    <Button
                      size="lg"
                      className="w-full"
                      disabled={busy || chosenCount === 0 || deliveredBy === null}
                      onClick={() => dispatchClinic(group)}
                    >
                      {chosenCount === 0
                        ? "Tick what goes on the van"
                        : deliveredBy === null
                          ? "Say who is delivering"
                          : `Send ${chosenCount} ${plural(
                              chosenCount,
                              "item",
                            )} to ${group.clinicName}`}
                    </Button>
                  </div>
                </CollapsibleSection>
              );
            })
          )}
        </div>

        <section className="mt-5 space-y-2 lg:sticky lg:top-[calc(var(--header-h)+2rem)] lg:mt-0">
          <SubHeading>Recently dispatched</SubHeading>
          {recent.length === 0 ? (
            <Empty
              title="Nothing dispatched yet"
              body="Deliveries you send appear here with who took them and when."
            />
          ) : (
            /* Stock `<Card>` with its own padding and gap emptied — the rows
               below own the geometry. */
            <UiCard className="gap-0 divide-y overflow-hidden p-0 lg:max-h-[calc(100dvh-12rem)] lg:overflow-y-auto">
              {recent.map((dispatch) => (
                <div
                  key={dispatch._id}
                  className="flex items-start gap-3 px-3 py-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold">
                      {dispatch.number} · {dispatch.clinicName}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {dispatch.lineCount} {plural(dispatch.lineCount, "item")} ·{" "}
                      {dispatch.totalUnits} {plural(dispatch.totalUnits, "unit")} ·{" "}
                      {relativeDay(dispatch.dispatchedAt)}
                      {dispatch.invoiceNumber ? ` · ${dispatch.invoiceNumber}` : ""}
                    </div>
                    {/* Both of these were resolved by dispatch:list and rendered
                        nowhere, so "who took delivery, and when?" — the question
                        asked when a clinic says it never arrived — was answerable
                        only from the database. */}
                    <div className="text-xs text-muted-foreground">
                      {dispatch.deliveredBy ? `Delivered by ${dispatch.deliveredBy}` : null}
                      {dispatch.receivedAt !== null
                        ? ` · received ${relativeDay(dispatch.receivedAt)}${
                            dispatch.receivedByName ? ` by ${dispatch.receivedByName}` : ""
                          }`
                        : " · not yet confirmed"}
                    </div>
                  </div>
                  <div className="shrink-0">
                    <StatusPill status={dispatch.receiptStatus} kind="receipt" />
                  </div>
                </div>
              ))}
            </UiCard>
          )}
        </section>
      </div>

      <Sheet
        open={confirmShort !== null}
        onClose={() => setConfirmShort(null)}
        title="Not enough on the shelf"
        description={
          confirmShort
            ? `${confirmShort.itemName} — ${confirmShort.qty} asked for, ${confirmShort.onHand} on hand.`
            : undefined
        }
        footer={
          <div className="space-y-2">
            <Button
              size="lg"
              className="w-full"
              onClick={() => {
                if (confirmShort) toggle(confirmShort.id);
                setConfirmShort(null);
              }}
            >
              Send it anyway
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="w-full"
              onClick={() => setConfirmShort(null)}
            >
              Leave it off the van
            </Button>
          </div>
        }
      >
        <p className="text-sm text-muted-foreground">
          Sending it takes the count below zero, and the clinic is told the full
          quantity is on its way. If the box is really on the shelf, book it in on
          Warehouse first so the count matches what you can see.
        </p>
      </Sheet>
    </div>
  );
}
